import React, { useEffect, useState } from 'react'
import {
  TextStyle,
  Image,
  TouchableOpacity,
  ActivityIndicator,
  View,
  ViewStyle,
  ImageStyle,
} from 'react-native'
import { observer } from 'mobx-react-lite'
import { useHistory } from 'react-router-dom'
import { Instance } from 'mobx-state-tree'
import { DateTime } from 'luxon'

import { AdaptiveTopic, load, save, setPageTitle, useStores } from '@trivie/core'
import { Card, color, Header, iconLeaderboard, iconTrophy, SvgIcon, Text } from '@trivie/ui-mobile'
import { TopicWheel } from '@trivie/ui-mobile/components/recall-wheel/topic-wheel'
import { GettingStarted } from '@trivie/ui-mobile/components/quiz-row/getting-started'
import { Box } from '@mui/material'

import hooray from './hooray.png'
import { ReactComponent as PlayNull } from './play-null.svg'

import { UpNextList } from './up-next-list'
import { Dialog } from '@trivie/ui-web/components/dialogs/dialog'
import { SubscriptionLockedScreen } from '@trivie/ui-mobile/components/subscription-locked-screen'

export const PlayScreen = observer(() => {
  const {
    subscriptionStore,
    leaderboardStore,
    resourceStore,
    quizStore,
    userStore,
    contestStore,
    surveyStore,
  } = useStores()
  const { getContestsForUser, contestsForUser } = contestStore
  const { adaptive, getAdaptive, isLoadingAdaptive } = quizStore
  const { markAsViewed, setEndViewed, setStartViewed, getSourceDetails } = resourceStore
  const { user, sortedUpNext, loadingUpNext, getUpNext, getUser } = userStore
  const { tags, getTagLeaderboards } = leaderboardStore
  const [viewingTextSource, setViewingTextSource] = useState<any>()

  const history = useHistory()

  const userLeaderboardsDisabled =
    (!user?.workspace_settings?.user_leaderboard_alltime_enabled &&
      !user?.workspace_settings?.user_leaderboard_monthly_enabled &&
      !user?.workspace_settings?.user_leaderboard_weekly_enabled &&
      !user?.workspace_settings?.user_leaderboard_daily_enabled) ||
    user?.workspace_settings?.gdpr_enabled

  const [leaderboardsDisabled, setLeaderboardsDisabled] = useState(userLeaderboardsDisabled)

  const [shownInitial, setShownInitial] = useState(false)

  const init = () => {
    if (user) {
      onFocus()
      getUser(user.id)
      getUpNext()
      getAdaptive(user.id)
      getContestsForUser(user.id)
      userStore.getBranding(user.id)
      getTagLeaderboards(user.id).then(() => {
        if (tags.length > 0) {
          setLeaderboardsDisabled(false)
        }
      })
    }
  }

  useEffect(() => {
    window.addEventListener('focus', onFocus)
    setPageTitle(`Play - ${process.env.REACT_APP_SITE_TITLE}`)
    load('shown_initial').then((data) => {
      setShownInitial(data)
      save('shown_initial', true)
    })
    init()
    return () => window.removeEventListener('focus', onFocus)
  }, [])

  const goToLeaderboard = () => history.push('/play/leaderboards')

  const startTutorial = () => {
    history.replace('/game/tutorial')
  }

  const startAdaptive = () => {
    history.push('/game/reinforcement')
  }

  const startKnowledgeBooster = (item: Instance<typeof AdaptiveTopic>) => {
    history.push(`/game/reinforcement/?topic_id=${item.topic_id}`)
  }

  const startSurvey = (tile: any) => {
    if (user) {
      history.push(
        `/survey/?survey_id=${tile.object_id}&content_user_id=${tile.metadata.content_user_id}&subscription_user_id=${tile.metadata.subscription_user_id}&path_id=${tile.metadata.path_id}&assignment_id=${tile.metadata.assignment_id}`,
      )
    }
  }

  const onFocus = () => {
    const resourceData = localStorage.getItem('viewing-resource')
    if (resourceData) {
      const resource = JSON.parse(resourceData)
      if (resourceData) {
        localStorage.removeItem('viewing-resource')
        setEndViewed(DateTime.now().toUTC().toISO())
        markAsViewed(resource.resource_id, resource.metadata)
      }
    }
  }

  const onTilePress = (tile: any) => {
    switch (tile.object_type.toLowerCase()) {
      case 'quiz':
        if (tile.metadata.subscription_user_id) {
          history.push(
            `/library/quiz/?subscription_user_id=${tile.metadata.subscription_user_id}&topic_id=${tile.metadata.topic_id}&quiz_id=${tile.object_id}&path_id=${tile.metadata.path_id}&content_user_id=${tile.metadata.content_user_id}`,
          )
        } else {
          history.push(
            `/library/quiz/?assignment_id=${tile.metadata.assignment_id}&topic_id=${tile.metadata.topic_id}&quiz_id=${tile.object_id}&path_id=${tile.metadata.path_id}&content_user_id=${tile.metadata.content_user_id}`,
          )
        }

        break
      case 'assessment':
        history.push(
          `/library/topic/?assignment_id=${tile.metadata.assignment_id}&topic_id=${tile.metadata.topic_id}&path_id=${tile.metadata.path_id}&content_user_id=${tile.metadata.content_user_id}`,
        )
        break
      case 'survey':
        startSurvey(tile)
        break
      case 'source':
        // Set start viewing and mark resource as viewed on return to tab using above onFocus
        setStartViewed(DateTime.now().toUTC().toISO())
        getSourceDetails(tile.object_id).then((data) => {
          if (data) {
            if (data?.raw_text) {
              setViewingTextSource({ ...data, metadata: tile.metadata })
            } else if (data?.isVideo) {
              localStorage.setItem(
                'viewing-resource',
                JSON.stringify({ resource_id: data.resource_id, metadata: tile.metadata }),
              )
              history.push(
                `/library/source/?source_id=${data.source_id}&assignment_id=${tile.metadata.assignment_id}&content_user_id=${tile.metadata.content_user_id}&path_id=${tile.metadata.path_id}&quiz_id=${tile.metadata.quiz_id}&topic_id=${tile.metadata.topic_id}`,
              )
            } else {
              localStorage.setItem(
                'viewing-resource',
                JSON.stringify({ resource_id: data.resource_id, metadata: tile.metadata }),
              )
              window.open(
                // @ts-ignore
                data.converted_pdf_url ? data.converted_pdf_url : data.filename_or_url,
                '_blank',
                'noopener',
              )
            }
          }
        })
        break
      default:
        break
    }
  }

  const goToContests = () => history.push('/play/contests')

  const buttonStyle = {
    flex: 1,
    marginBottom: 20,
    height: 76,
    borderRadius: 16,
  }

  const isSubscriptionWorkspace = userStore.isSubscriptionWorkspace
  const hasActive = subscriptionStore.hasActiveSubscriptions

  if (isSubscriptionWorkspace && !hasActive) {
    return (
      <View testID="talk-screen" style={{ flex: 1, height: '100%', maxWidth: 800 }}>
        <div
          style={{
            height: '100%',
            flexDirection: 'column',
            justifyContent: 'center',
            display: 'flex',
          }}
        >
          <div style={HEADER_STICKY_CONTAINER}>
            <div style={HEADER_FLEX_CONTAINER}>
              <Header titleStyle={HEADER} headerTx="Play.header" />
            </div>
          </div>
          <SubscriptionLockedScreen
            emoji="📚"
            title="No active subscriptions"
            helperText="Purchase or renew a subscription to start learning"
            buttonText="Browse Products"
            onButtonPress={
              userStore.externalStoreUrl
                ? () => {
                    window.open(
                      // @ts-ignore
                      userStore.externalStoreUrl,
                      '_blank',
                      'noopener',
                    )
                  }
                : undefined
            }
          />
        </div>
      </View>
    )
  }

  return (
    <div style={ROOT}>
      <div style={HEADER_STICKY_CONTAINER}>
        <div style={HEADER_FLEX_CONTAINER}>
          <Header titleStyle={HEADER} headerTx="Play.header" />
        </div>
      </div>
      {isLoadingAdaptive || loadingUpNext ? (
        <View style={{ height: '100%', top: -100, justifyContent: 'center', display: 'flex' }}>
          <ActivityIndicator size="small" />
        </View>
      ) : (
        <div>
          {leaderboardsDisabled && contestsForUser?.length === 0 ? null : (
            <View style={TOP_NAV_CONTAINER}>
              <>
                {contestsForUser?.length > 0 && (
                  <TouchableOpacity
                    // @ts-ignore
                    dataSet={{ qat: 'contests_btn' }}
                    onPress={goToContests}
                    style={{ marginRight: 6, ...buttonStyle }}
                  >
                    <View style={NAV_GRADIENT}>
                      <View style={{ alignSelf: 'center', paddingTop: 17, top: 4, left: 4 }}>
                        <SvgIcon size={32} viewBox="0 0 32 32" src={iconTrophy} />
                      </View>
                      <Text
                        style={{ paddingTop: 4, color: color.shade50 }}
                        variant="body2Emphasized"
                        tx="Contests.header"
                        align="center"
                      />
                    </View>
                  </TouchableOpacity>
                )}
                {!leaderboardsDisabled && (
                  <TouchableOpacity
                    // @ts-ignore
                    dataSet={{ qat: 'leaderboard_btn' }}
                    onPress={goToLeaderboard}
                    style={{ marginLeft: contestsForUser?.length === 0 ? 0 : 6, ...buttonStyle }}
                  >
                    <View style={NAV_GRADIENT}>
                      <View style={{ alignSelf: 'center', paddingTop: 17 }}>
                        <SvgIcon size={32} viewBox="0 0 32 32" src={iconLeaderboard} />
                      </View>
                      <Text
                        style={{ paddingTop: 4, color: color.shade50 }}
                        variant="body2Emphasized"
                        tx="Leaderboard.header"
                        align="center"
                      />
                    </View>
                  </TouchableOpacity>
                )}
              </>
            </View>
          )}
          {quizStore.showTutorial && (
            <View style={{ marginBottom: 20, height: 73 }}>
              <GettingStarted
                tutorialName="Trivie"
                startTutorial={() => startTutorial()}
                style={{ width: '100%' }}
              />
            </View>
          )}
          {sortedUpNext?.length === 0 && shownInitial ? (
            <Card
              style={{
                width: 335,
                height: 254,
                justifyContent: 'center',
                flexDirection: 'column',
                marginBottom: 32,
              }}
            >
              <img style={{ marginBottom: 15 }} src={hooray} />
              <Text
                style={{ textAlign: 'center', marginBottom: 5 }}
                tx="Quiz.caughtUp"
                variant="titleEmphasized"
              />
              <Text
                style={{ textAlign: 'center', color: color.shade50 }}
                tx="Quiz.fresh"
                variant="body2"
              />
            </Card>
          ) : sortedUpNext?.length === 0 ? null : (
            <UpNextList
              onTilePress={onTilePress}
              style={{ marginBottom: 32 }}
              tiles={sortedUpNext}
              locale={user?.language ?? 'en'}
              options={{ containScroll: false, align: 'start', skipSnaps: true, slidesToScroll: 3 }}
            />
          )}
          <div>
            <Text
              tx="Topic.knowledgeBoost"
              variant="body1Emphasized"
              style={{ color: color.shade70, marginRight: 8, marginBottom: 14 }}
            />
            <View>
              {adaptive && adaptive.has_quiz && (
                <Card
                  // @ts-ignore
                  dataSet={{ qat: 'reinforcement_quiz_btn' }}
                  onPress={startAdaptive}
                  style={ADAPTIVE}
                >
                  <View>
                    <Text
                      style={{ color: 'rgb(206,0,253)' }}
                      variant="titleEmphasized"
                      tx="Quiz.reinforce"
                    />
                    <Text
                      tx="Quiz.reinforcementSubtitle"
                      style={{
                        color: 'rgb(206,0,253)',
                        fontWeight: '700',
                        fontSize: 12,
                        lineHeight: 14.52,
                      }}
                    />
                  </View>
                  <View>
                    <svg
                      width="42"
                      height="42"
                      viewBox="0 0 42 42"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <g opacity="0.8">
                        <circle cx="21.0001" cy="20.9991" r="17.2667" fill="white" />
                        <circle
                          cx="21.0001"
                          cy="20.9991"
                          r="16.5917"
                          stroke="white"
                          strokeOpacity="0.3"
                          strokeWidth="1.35"
                        />
                      </g>
                      <path
                        d="M19.9777 30.9123V23.9639H16.0951C15.5551 23.9639 15.1902 23.2925 15.4529 22.7387L20.8242 10.7048C21.1599 9.91592 22.1963 10.2012 22.1963 11.0908V18.1231H25.9036C26.4437 18.1231 26.794 18.7777 26.5605 19.3316L21.3643 31.2816C21.014 32.0872 19.9777 31.8019 19.9777 30.9123Z"
                        fill="url(#paint0_linear_2_4)"
                      />
                      <path
                        d="M42 21C42 32.598 32.598 42 21 42C9.40202 42 0 32.598 0 21C0 9.40202 9.40202 0 21 0C32.598 0 42 9.40202 42 21ZM4.2 21C4.2 30.2784 11.7216 37.8 21 37.8C30.2784 37.8 37.8 30.2784 37.8 21C37.8 11.7216 30.2784 4.2 21 4.2C11.7216 4.2 4.2 11.7216 4.2 21Z"
                        fill="url(#paint1_linear_2_4)"
                      />
                      <defs>
                        <linearGradient
                          id="paint0_linear_2_4"
                          x1="17.1252"
                          y1="31.0759"
                          x2="32.1644"
                          y2="25.6915"
                          gradientUnits="userSpaceOnUse"
                        >
                          <stop stopColor="#FF00FF" />
                          <stop offset="0.46" stopColor="#9116F9" />
                          <stop offset="1" stopColor="#3199FF" />
                        </linearGradient>
                        <linearGradient
                          id="paint1_linear_2_4"
                          x1="6.55797"
                          y1="40.6723"
                          x2="49.6619"
                          y2="11.2111"
                          gradientUnits="userSpaceOnUse"
                        >
                          <stop stopColor="#FFB2FF" />
                          <stop offset="0.46" stopColor="#DCB4FE" />
                          <stop offset="1" stopColor="#A4D2FF" />
                        </linearGradient>
                      </defs>
                    </svg>
                  </View>
                </Card>
              )}
              {adaptive?.adaptive_topics
                .slice()
                .sort((a: any, b: any) => a.recall - b.recall)
                .map((item, index) => {
                  return (
                    <Card
                      // @ts-ignore
                      dataSet={{ qat: `knowledge_booster_quiz_${index}` }}
                      onPress={() => startKnowledgeBooster(item)}
                      style={KNOWLEDGE_BOOSTER}
                    >
                      <View>
                        <Text
                          style={{ marginBottom: 4 }}
                          text={item.topic_title}
                          variant="titleEmphasized"
                        />
                        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                          <Text
                            style={{
                              color: color.orange,
                              fontWeight: '700',
                              fontSize: 12,
                              lineHeight: 14.52,
                            }}
                            text={`${Math.round(item.recall ? item.recall * 100 : 0)}% `}
                          />
                          <Text
                            tx="Profile.recall"
                            style={{
                              color: '#9098A3',
                              fontWeight: '700',
                              fontSize: 12,
                              lineHeight: 14.52,
                            }}
                          />
                        </View>
                      </View>
                      <View>
                        <Image style={TOPIC_IMG} source={{ uri: item.image_url ?? '' }} />
                        <TopicWheel id={item.topic_id} value={item.recall} image={item.image_url} />
                      </View>
                    </Card>
                  )
                })}
            </View>
            {sortedUpNext.length === 0 &&
              !shownInitial &&
              adaptive?.adaptive_topics.length === 0 &&
              !adaptive?.has_quiz && (
                <View style={{ flexDirection: 'column', marginBottom: 32 }}>
                  <Text tx="Quiz.upNext" variant="body1Emphasized" style={UP_NEXT} />
                  <Text
                    tx="Quiz.null"
                    variant="body2"
                    style={{ color: color.shade50, marginRight: 8 }}
                  />
                </View>
              )}
            {adaptive?.adaptive_topics.length === 0 && !adaptive?.has_quiz && (
              <View style={{ flexDirection: 'column', marginBottom: 32 }}>
                <Text
                  tx="Topic.knowledgeBoostTagline"
                  variant="body2"
                  style={{ color: color.shade50, marginRight: 8 }}
                />
              </View>
            )}
            {sortedUpNext.length === 0 &&
              !shownInitial &&
              adaptive?.adaptive_topics.length === 0 &&
              !adaptive?.has_quiz && (
                <View style={{ flexDirection: 'row', justifyContent: 'center' }}>
                  <PlayNull />
                </View>
              )}
          </div>
        </div>
      )}
      <Dialog
        maxWidth="sm"
        align="left"
        onClose={() => {
          setViewingTextSource(null)
          setEndViewed(DateTime.now().toUTC().toISO())
          markAsViewed(viewingTextSource?.resource_id, viewingTextSource?.metadata)
          init()
        }}
        title="Preview"
        open={Boolean(viewingTextSource)}
      >
        <Box flexDirection="column" display="flex" p={2} sx={{ whiteSpace: 'pre-wrap' }}>
          <Text style={{ marginBottom: 16 }} variant="headline" text={viewingTextSource?.title} />
          {viewingTextSource?.raw_text}
        </Box>
      </Dialog>
    </div>
  )
})

/**
 * Styling
 */
const ROOT: React.CSSProperties = {
  backgroundColor: color.white,
  height: '100%',
  maxWidth: 800,
}
const HEADER_STICKY_CONTAINER: React.CSSProperties = {
  position: 'sticky',
  top: 0,
  width: '100%',
  zIndex: 99,
  backgroundColor: 'white',
}
const HEADER_FLEX_CONTAINER: React.CSSProperties = {
  display: 'flex',
  maxWidth: '1440px',
  justifyContent: 'space-between',
}
const HEADER: TextStyle = {
  color: color.black,
  display: 'flex',
  minHeight: 70,
  justifyContent: 'flex-start',
  paddingRight: 20,
  alignItems: 'center',
}
const UP_NEXT: TextStyle = { color: color.shade70, marginRight: 8, marginBottom: 14 }
const KNOWLEDGE_BOOSTER: ViewStyle = {
  height: 84,
  paddingRight: 23,
  paddingLeft: 23,
  flexDirection: 'row',
  justifyContent: 'space-between',
  marginBottom: 12,
}
const TOPIC_IMG: ImageStyle = {
  position: 'absolute',
  height: 30,
  width: 30,
  borderRadius: 100,
  left: 6,
  top: 6,
}
const ADAPTIVE: ViewStyle = {
  height: 84,
  paddingRight: 23,
  paddingLeft: 23,
  flexDirection: 'row',
  justifyContent: 'space-between',
  marginBottom: 12,
}
const TOP_NAV_CONTAINER: ViewStyle = {
  flex: 1,
  flexDirection: 'row',
  justifyContent: 'center',
}
const NAV_GRADIENT: ViewStyle = {
  height: 76,
  borderRadius: 16,
  justifyContent: 'center',
  paddingBottom: 12,
  backgroundColor: color.white,
  borderWidth: 1,
  borderColor: '#EFF0F6',
}
